/**
 * @fileoverview Authentication client for interacting with the auth service.
 * This file implements the client that makes real-time authentication and
 * authorization requests to the auth service without any local caching.
 *
 * Extends GraphQLClientBase for consistent retry logic and error handling.
 */

import { ErrorAuthentication } from "@eoln/errors";
import { GraphQLClientBase, type GraphQLClientOptions } from "@eoln/graphql-client";
import { isValidApiKeyFormat } from "../src/apiKeyUtils.ts";
import type { ApiKey, ApiKeyCreationResponse } from "../types/api-key.ts";

/**
 * GraphQL response types for type-safe response handling.
 */
interface CreateApiKeyResponse {
  createApiKey: ApiKeyCreationResponse;
}

interface RevokeApiKeyResponse {
  revokeApiKey: boolean;
}

interface ListApiKeysResponse {
  apiKeys: {
    apiKeys: ApiKey[];
    total: number;
    hasMore: boolean;
  };
}

interface AuthInfoResponse {
  authInfo: { isAuthenticated: boolean };
}

interface HasScopesResponse {
  hasScopes: { has: boolean; scopes: string[] | null };
}

interface GetApiKeyResponse {
  apiKey: ApiKey | null;
}

/**
 * Configuration options for the AuthClient.
 */
export interface AuthClientOptions extends Omit<GraphQLClientOptions, "endpoint"> {
  /**
   * URL of the GraphQL API endpoint for the auth service.
   * Default: http://localhost:8001/graphql
   */
  endpoint?: string;
}

/**
 * Input for creating a new API key.
 */
export interface CreateApiKeyInput {
  /** Human-readable name to identify the key's purpose or owner */
  name: string;

  /** Optional expiration date for the key */
  expiresAt?: Date;

  /** Optional list of scopes/permissions for this key */
  scopes?: string[];
}

/**
 * Client for interacting with the auth service.
 *
 * This class provides methods for:
 * - API key management (creating, revoking, listing)
 * - Authentication and authorization utilities
 *
 * Features:
 * - Retry with exponential backoff for transient failures (via @eoln/resilience)
 * - Consistent error handling (ErrorAuthentication, ErrorAuthorization)
 * - Health metrics for monitoring retry behavior
 *
 * @example
 * ```typescript
 * const client = new AuthClient({
 *   endpoint: 'https://auth.api.example.com/graphql',
 *   apiKey: 'sk_...',
 * });
 *
 * // Create an API key
 * const newKey = await client.createApiKey({
 *   name: 'My App',
 *   scopes: ['invoices:read', 'invoices:write'],
 * });
 *
 * // Check health metrics
 * console.log(client.getHealthMetrics());
 * ```
 */
export class AuthClient extends GraphQLClientBase {
  /**
   * Creates a new AuthClient instance.
   *
   * @param options - Configuration options
   */
  constructor(options: AuthClientOptions = {}) {
    super({
      endpoint: options.endpoint ?? "http://localhost:8001/graphql",
      apiKey: options.apiKey,
      headers: options.headers,
      retry: options.retry,
    });

    if (this.apiKey && !isValidApiKeyFormat(this.apiKey)) {
      console.warn(
        "[AuthClient Warning]: Initial API key provided to constructor has invalid format. Expected sk_ followed by 64 hex characters.",
      );
    }
  }

  /**
   * Sets the API key for this client instance.
   *
   * @param apiKey - The API key to use for authentication
   */
  override setApiKey(apiKey: string): void {
    if (!isValidApiKeyFormat(apiKey)) {
      console.warn(
        "[AuthClient Warning]: API key being set has invalid format. Expected sk_ followed by 64 hex characters.",
      );
    }
    super.setApiKey(apiKey);
  }

  /**
   * Creates a new API key.
   *
   * @param input - Configuration for the new API key
   * @returns Created API key with the secret key value
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async createApiKey(input: CreateApiKeyInput): Promise<ApiKeyCreationResponse> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation CreateApiKey($input: CreateApiKeyInput!) {
        createApiKey(input: $input) {
          apiKeyId
          name
          expiresAt
          createdAt
          active
          scopes
          key
        }
      }
    `;

    const variables = { input };

    const response = await this.graphqlRequest<CreateApiKeyResponse>(query, variables);
    return response.createApiKey;
  }

  /**
   * Revokes (deactivates) an API key.
   *
   * @param apiKeyId - The ID of the API key to revoke
   * @returns True if successful
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async revokeApiKey(apiKeyId: string): Promise<boolean> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation RevokeApiKey($apiKeyId: UUID!) {
        revokeApiKey(apiKeyId: $apiKeyId)
      }
    `;

    const variables = { apiKeyId };

    const response = await this.graphqlRequest<RevokeApiKeyResponse>(query, variables);
    return response.revokeApiKey;
  }

  /**
   * Lists all active API keys with pagination.
   *
   * @param limit - Maximum number of keys to return (default: 50, max: 100)
   * @param offset - Number of keys to skip (default: 0)
   * @returns Paginated response with API keys, total count, and hasMore flag
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async listApiKeys(limit = 50, offset = 0): Promise<{ apiKeys: ApiKey[]; total: number; hasMore: boolean }> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query GetApiKeys($pagination: ApiKeyPaginationInput!) {
        apiKeys(pagination: $pagination) {
          apiKeys {
            apiKeyId
            name
            expiresAt
            createdAt
            active
            scopes
          }
          total
          hasMore
        }
      }
    `;

    const variables = { pagination: { limit, offset } };

    const response = await this.graphqlRequest<ListApiKeysResponse>(query, variables);
    return response.apiKeys;
  }

  /**
   * Gets information about the current authentication status.
   *
   * This makes a real-time call to the auth service to validate the API key.
   * No authentication state is cached, ensuring the most up-to-date authentication status.
   *
   * @returns Auth information including whether authenticated
   */
  async getAuthInfo(): Promise<{ isAuthenticated: boolean }> {
    const query = `
      query GetAuthInfo {
        authInfo {
          isAuthenticated
        }
      }
    `;

    const response = await this.graphqlRequest<AuthInfoResponse>(query);
    return response.authInfo;
  }

  /**
   * Checks if the current API key has all the specified scopes.
   *
   * This makes a real-time call to the auth service to check permissions.
   * No permission state is cached, ensuring the most up-to-date scope validation.
   *
   * @param scopes - Array of scope strings to check
   * @returns Object with 'has' boolean and the scopes the key actually has
   * @throws {ErrorAuthentication} If not authenticated
   */
  async hasScopes(scopes: string[]): Promise<{ has: boolean; scopes: string[] | null }> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query HasScopes($scopes: [String!]!) {
        hasScopes(scopes: $scopes) {
          has
          scopes
        }
      }
    `;

    const variables = { scopes };

    const response = await this.graphqlRequest<HasScopesResponse>(query, variables);
    return response.hasScopes;
  }

  /**
   * Gets a specific API key by ID.
   *
   * @param apiKeyId - The ID of the API key to get
   * @returns The API key if found, null otherwise
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async getApiKey(apiKeyId: string): Promise<ApiKey | null> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query GetApiKey($apiKeyId: UUID!) {
        apiKey(apiKeyId: $apiKeyId) {
          apiKeyId
          name
          expiresAt
          createdAt
          active
          scopes
        }
      }
    `;

    const variables = { apiKeyId };

    const response = await this.graphqlRequest<GetApiKeyResponse>(query, variables);
    return response.apiKey;
  }

  /**
   * Validates an API key by making an authenticated request.
   *
   * @param apiKey - The API key to validate
   * @returns True if the API key is valid
   */
  async validateApiKey(apiKey: string): Promise<boolean> {
    const previousKey = this.apiKey;
    try {
      this.apiKey = apiKey;
      const authInfo = await this.getAuthInfo();
      return authInfo.isAuthenticated;
    } catch {
      return false;
    } finally {
      this.apiKey = previousKey;
    }
  }

  /**
   * Validates an API key and returns the key data if valid
   * @param apiKey - The API key to validate
   * @returns The API key data if valid, null otherwise
   */
  async getValidatedApiKey(apiKey: string): Promise<ApiKey | null> {
    const previousKey = this.apiKey;
    try {
      this.apiKey = apiKey;
      console.log("[AuthClient] Calling getAuthInfoWithKey with endpoint:", this.endpoint);
      const authInfo = await this.getAuthInfoWithKey();
      console.log("[AuthClient] getAuthInfoWithKey result:", {
        isAuthenticated: authInfo.isAuthenticated,
        hasApiKey: !!authInfo.apiKey,
      });
      return authInfo.isAuthenticated ? authInfo.apiKey : null;
    } catch (error) {
      console.error("[AuthClient] Error in getValidatedApiKey:", error);
      return null;
    } finally {
      this.apiKey = previousKey;
    }
  }

  /**
   * Gets authentication info including the current API key
   * @returns Authentication info with the current API key
   */
  async getAuthInfoWithKey(): Promise<{ isAuthenticated: boolean; apiKey: ApiKey | null }> {
    const query = `
      query GetAuthInfoWithKey {
        authInfo {
          isAuthenticated
          apiKey {
            apiKeyId
            name
            scopes
            expiresAt
            createdAt
            active
          }
        }
      }
    `;

    const response = await this.graphqlRequest<{
      authInfo: { isAuthenticated: boolean; apiKey: ApiKey | null };
    }>(query);
    return response.authInfo;
  }
}
